import React from 'react';
import BrandLogo from '@/components/UI/BrandLogo';
import { cn } from '@/utils/cn';

interface LeftSidePanelProps {
  /** @deprecated The panel is derived from the base colour (`--ui-chrome-*` tokens), never the primary. Ignored. */
  primaryColor?: string;
  /** Flat background override (any CSS color). Replaces the brand gradient. */
  background?: string;
  /** Tenant/app name displayed next to a custom `logoIcon`. Defaults to "Smart\nContractor". */
  appName?: string;
  /** Optional logo override — rendered instead of the RoofVox lockup. */
  logoIcon?: React.ReactNode;
  className?: string;
  /** Panel width in px. Defaults to 280. */
  width?: number;
  /**
   * Interactive body (e.g. navigation links). When provided, the panel renders
   * these in place of the decorative feature callouts and becomes a real,
   * focusable navigation region (no longer aria-hidden / lg-only).
   */
  children?: React.ReactNode;
  /** Optional bottom slot pinned below the body (e.g. a Settings link). */
  footer?: React.ReactNode;
  /** @deprecated The panel is always dark in both colour modes. Ignored. */
  lightMode?: boolean;
}

// ── Feature callouts (decorative, shown on the auth pages) ───────────────────────────────────────

const iconProps = {
  viewBox: '0 0 24 24',
  width: 20,
  height: 20,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const NAV_ITEMS = [
  {
    label: 'Project Tracking',
    description: 'Manage jobs, crews & timelines in one place.',
    icon: (
      <svg {...iconProps}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  {
    label: 'Voice-First Design',
    description: 'Let SAM handle notes, tasks & updates for you.',
    icon: (
      <svg {...iconProps}>
        <line x1="4" y1="10" x2="4" y2="14" />
        <line x1="8" y1="6" x2="8" y2="18" />
        <line x1="12" y1="3" x2="12" y2="21" />
        <line x1="16" y1="7" x2="16" y2="17" />
        <line x1="20" y1="10" x2="20" y2="14" />
      </svg>
    ),
  },
  {
    label: 'Secure & Compliant',
    description: 'Enterprise-grade security with full audit trails.',
    icon: (
      <svg {...iconProps}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

// ── Component ────────────────────────────────────────────────────────────────────────────────────

const LeftSidePanel: React.FC<LeftSidePanelProps> = ({
  background,
  appName = 'Smart\nContractor',
  logoIcon,
  className = '',
  width = 280,
  children,
  footer,
}) => {
  const interactive = children != null;
  const nameLines = appName.split('\n');

  return (
    <div
      className={cn(
        interactive ? 'flex h-full' : 'hidden min-h-dvh lg:flex',
        'relative flex-shrink-0 select-none flex-col overflow-hidden border-r border-white/10 text-chrome-text',
        className,
      )}
      style={{
        width,
        // Brand "Navy -> Steel" (partway), from the base colour. Never the tenant's primary.
        background:
          background ?? 'linear-gradient(168deg, rgb(var(--ui-chrome-from)) 0%, rgb(var(--ui-chrome-to)) 100%)',
      }}
      aria-hidden={interactive ? undefined : 'true'}
      data-left-side-panel=""
    >
      {/* One static violet glow (the brand's "AI glow"); no cursor tracking, no decorative rings. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full"
        style={{ background: 'radial-gradient(closest-side, rgb(var(--ui-accent) / 0.26), transparent)' }}
      />

      {/* Logo area. A custom logoIcon (e.g. a tenant logo) renders next to the app name; the RoofVox lockup
          already includes its wordmark and stands alone. */}
      <div className="relative z-[2] flex h-[63px] flex-shrink-0 items-center px-5" style={{ minWidth: width, gap: logoIcon ? 20 : 0 }}>
        {logoIcon ? (
          <>
            <div className="h-10 w-10 flex-shrink-0">{logoIcon}</div>
            <span
              className="min-w-0 flex-1 overflow-hidden text-base font-bold leading-tight text-chrome-text"
              style={{
                // Long tenant names wrap (and break mid-word if needed) instead of overflowing; 3 lines max.
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }}
            >
              {nameLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < nameLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          </>
        ) : (
          <BrandLogo surface="dark" height={32} />
        )}
      </div>

      <div className="relative z-[2] mx-4 mb-3 h-px flex-shrink-0 bg-white/10" style={{ minWidth: width - 32 }} />

      {/* Body: interactive nav (children) or decorative feature callouts */}
      {interactive ? (
        <nav className="no-scrollbar relative z-[2] flex-1 overflow-y-auto px-3 py-1">{children}</nav>
      ) : (
        <nav className="relative z-[2] flex-1 overflow-hidden">
          <ul className="flex list-none flex-col gap-1 px-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>
                <div className="flex cursor-default items-start gap-3.5 rounded-2xl px-3 py-2.5">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-chrome-accent">
                    {item.icon}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold leading-snug text-chrome-text">{item.label}</span>
                    <span className="text-xs leading-snug text-chrome-text2">{item.description}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {footer && <div className="relative z-[2] flex-shrink-0 border-t border-white/10 px-3 py-3 md:flex md:h-20 md:items-center md:py-0 md:[&>*]:w-full">{footer}</div>}
    </div>
  );
};

export default LeftSidePanel;
